import { isSearchResultsPath } from '@seek/seek-jobs-seo';
import { Box, Stack, useResponsiveValue, PageBlock } from 'braid-design-system';
import { renderRoutes } from 'react-router-config';
import Sticky from 'react-sticky-el';
// @ts-expect-error: non-ts file
import { provideHooks } from 'redial';
import type { RedialLocals } from 'src/types/RedialLocals';
import SaveSearchPrompt from 'src/components/SaveSearchPrompt/SaveSearchPrompt';
import StickySerpElement from 'src/components/StickySerpElement/StickySerpElement';
import type { TSeekExperiments } from 'src/store/experiments/experimentHelpers';
import { useSelector } from 'src/store/react';
import { getCounts, updateCriteria } from 'src/store/search/search';
import {
  selectAuthenticated,
  selectIsFiltersExpanded,
  selectIsResultsHidden,
  selectLocation,
  selectSaveSearchStatus,
} from 'src/store/selectors';
import SearchBar from './SearchBar/SearchBar';
import RefineBar from './RefineBar/RefineBar';
import useFetchSavedSearches from 'src/modules/hooks/SavedSearch';
import * as styles from './Search.css';
import { useIsSplitViewEnabled } from 'src/store/featureFlags/hooks';

const hooks = {
  first({ dispatch, path = '', query, zone }: RedialLocals) {
    return dispatch(
      updateCriteria({
        path,
        query,
        zone,
      }),
    );
  },
  defer({ dispatch, country, path = '', query, cookies, zone }: RedialLocals) {
    return dispatch(
      getCounts({
        zone,
        country,
        path,
        query,
        cookies,
      }),
    );
  },
};

export interface SearchProps {
  route: Record<string, any>;
  experiments?: TSeekExperiments;
}

const Search = ({ route }: SearchProps) => {
  const authenticated = useSelector(selectAuthenticated);
  const isSplitView = useIsSplitViewEnabled();
  const filtersExpanded = useSelector(selectIsFiltersExpanded);
  const resultsHidden = useSelector(selectIsResultsHidden);
  const saveSearchStatus = useSelector(selectSaveSearchStatus);
  const { pathname } = useSelector(selectLocation);

  const { isSearchSaved } = useFetchSavedSearches();
  const showStickySaveSearchPrompt =
    isSearchResultsPath(pathname) &&
    !resultsHidden &&
    authenticated &&
    !isSplitView;

  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  return (
    <Box height="full">
      <Box
        position="relative"
        // This is to ensure this will not cover Header's language switcher
        zIndex={2}
        background="brand"
        paddingTop={{
          mobile: 'medium',
          tablet: 'large',
        }}
        paddingBottom={{
          mobile: 'medium',
          tablet: 'small',
        }}
        className={styles.branding}
      >
        <PageBlock width="medium">
          <Stack
            space={{
              mobile: filtersExpanded ? 'xsmall' : 'none',
              tablet: 'xsmall',
            }}
          >
            <SearchBar collapsed={filtersExpanded} />
            <RefineBar expanded={filtersExpanded} />
          </Stack>
        </PageBlock>
      </Box>

      {showStickySaveSearchPrompt && !isMobile && (
        <StickySerpElement target="sticky-save-search-desktop" topOffset={190}>
          <SaveSearchPrompt
            status={saveSearchStatus}
            isSearchSaved={isSearchSaved}
            shadowPosition="bottom"
          />
        </StickySerpElement>
      )}

      {renderRoutes(route.routes)}

      {showStickySaveSearchPrompt && isMobile && (
        <Box position="fixed" bottom={0} width="full" zIndex="sticky">
          <Sticky mode="bottom">
            <SaveSearchPrompt
              status={saveSearchStatus}
              isSearchSaved={isSearchSaved}
              shadowPosition="top"
            />
          </Sticky>
        </Box>
      )}
    </Box>
  );
};

export default provideHooks(hooks)(Search);
